import { Fragment, useEffect, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import {
    cancelFundTransfer,
    confirmFundTransfer,
    fetchFundTransferPostScreenshot,
    fetchFundTransferPreScreenshot,
    getFundTransfers,
    initiateFundTransfer,
} from '../../services/FundTransferService'
import { ROLE_SLUGS } from '../../utils/auth/permissionConstants'
import { scrollToPageTop } from '../../utils/scrollToPageTop'

const statusBadgeClass = (status) => {
    if (status === 'confirmed') return 'badge-success'
    if (status === 'pending')   return 'badge-warning'
    return 'badge-danger'
}

const statusLabel = (status) => status.charAt(0).toUpperCase() + status.slice(1)

const formatAmount = (amount) =>
    `KES ${Number(amount).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`

const formatDate = (dateString) =>
    new Date(dateString).toLocaleString('en-KE', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })

const DIRECTION_OPTIONS = [
    { value: 'utility_to_working', label: 'Utility → Working', sourceAccount: 'utility',  destinationAccount: 'working' },
    { value: 'working_to_utility', label: 'Working → Utility', sourceAccount: 'working', destinationAccount: 'utility'  },
]

const DIRECTION_STYLE = {
    utility_to_working: {
        color:      '#0d6efd',
        background: 'rgba(13, 110, 253, 0.12)',
        icon:       'bi-arrow-right-circle',
    },
    working_to_utility: {
        color:      '#c99509',
        background: 'rgba(255, 193, 7, 0.18)',
        icon:       'bi-arrow-left-circle',
    },
}

const formatDirection = (direction) =>
    DIRECTION_OPTIONS.find((o) => o.value === direction)?.label ?? direction

const DirectionBadge = ({ direction }) => {
    const style = DIRECTION_STYLE[direction]
    const label = formatDirection(direction)
    if (!style) return <span>{label}</span>
    return (
        <span
            className="badge badge-rounded d-inline-flex align-items-center"
            style={{
                background:  style.background,
                color:       style.color,
                fontWeight:  600,
                fontSize:    '0.72rem',
                padding:     '0.35rem 0.6rem',
            }}
        >
            <i className={`bi ${style.icon} me-1`} aria-hidden="true" />
            {label}
        </span>
    )
}

const EMPTY_INITIATE_FORM = { direction: 'utility_to_working', amount: '', description: '' }

const actorForStatus = (transfer) => {
    if (transfer.status === 'confirmed') return { role: 'Confirmed by', actor: transfer.confirmed_by }
    if (transfer.status === 'cancelled') return { role: 'Cancelled by', actor: transfer.cancelled_by }
    return { role: 'Initiated by', actor: transfer.initiated_by }
}

const ActorCell = ({ transfer }) => {
    const { role, actor } = actorForStatus(transfer)
    return (
        <div>
            <div className="small text-muted">{role}</div>
            <div style={{ overflowWrap: 'anywhere' }}>
                {actor?.name ?? '-'}
                {actor?.is_deleted && (
                    <span className="text-muted ms-1" style={{ fontSize: '0.75rem' }}>(deleted)</span>
                )}
            </div>
        </div>
    )
}

const StatusBadge = ({ status }) => (
    <span className={`badge badge-rounded ${statusBadgeClass(status)}`}>
        {statusLabel(status)}
    </span>
)

const PaginationBar = ({ page, lastPage, isLoading, onPrev, onNext }) => {
    if (lastPage <= 1) return null
    return (
        <div className="d-flex justify-content-center align-items-center gap-3 px-3 py-2 border-top">
            <button
                type="button"
                className="btn btn-sm btn-link p-0 text-decoration-none"
                disabled={page <= 1 || isLoading}
                onClick={onPrev}
                aria-label="Previous page"
            >
                <i className="fa fa-angle-left me-1" />
                Prev
            </button>
            <span className="text-muted small">Page {page} of {lastPage}</span>
            <button
                type="button"
                className="btn btn-sm btn-link p-0 text-decoration-none"
                disabled={page >= lastPage || isLoading}
                onClick={onNext}
                aria-label="Next page"
            >
                Next
                <i className="fa fa-angle-right ms-1" />
            </button>
        </div>
    )
}

const FundTransfers = () => {
    const queryClient = useQueryClient()
    const userRole = useSelector((state) => state.auth.authUser?.role_slug)

    const [showInitiateModal, setShowInitiateModal]           = useState(false)
    const [showConfirmModal, setShowConfirmModal]             = useState(false)
    const [showCancelModal, setShowCancelModal]               = useState(false)
    const [showViewModal, setShowViewModal]                   = useState(false)
    const [transferToAct, setTransferToAct]                   = useState(null)
    const [transferToView, setTransferToView]                 = useState(null)
    const [initiateForm, setInitiateForm]                     = useState(EMPTY_INITIATE_FORM)
    const [preTransferScreenshotFile, setPreTransferScreenshotFile]   = useState(null)
    const [postTransferScreenshotFile, setPostTransferScreenshotFile] = useState(null)
    const [page, setPage]                                     = useState(1)
    const [preScreenshotUrl, setPreScreenshotUrl]             = useState(null)
    const [postScreenshotUrl, setPostScreenshotUrl]           = useState(null)

    const { data, isPending, isError, isFetching } = useQuery({
        queryKey: ['fund-transfers', page],
        queryFn: () => getFundTransfers(page),
        placeholderData: keepPreviousData,
        staleTime: 0,
        enabled: userRole === ROLE_SLUGS.EXEC_ADMIN,
    })

    const transfers = data?.data?.data ?? []
    const lastPage  = data?.data?.meta?.last_page ?? 1

    const { data: preScreenshotBlob, isPending: preScreenshotPending } = useQuery({
        queryKey: ['fund-transfer-pre-screenshot', transferToView?.id],
        queryFn: () => fetchFundTransferPreScreenshot(transferToView.id),
        enabled: showViewModal && !!transferToView?.id,
        retry: false,
    })

    const { data: postScreenshotBlob, isPending: postScreenshotPending } = useQuery({
        queryKey: ['fund-transfer-post-screenshot', transferToView?.id],
        queryFn: () => fetchFundTransferPostScreenshot(transferToView.id),
        enabled: showViewModal && !!transferToView?.id && transferToView?.status !== 'pending',
        retry: false,
    })

    useEffect(() => {
        if (!preScreenshotBlob) return
        const url = URL.createObjectURL(preScreenshotBlob)
        setPreScreenshotUrl(url)
        return () => { URL.revokeObjectURL(url); setPreScreenshotUrl(null) }
    }, [preScreenshotBlob])

    useEffect(() => {
        if (!postScreenshotBlob) return
        const url = URL.createObjectURL(postScreenshotBlob)
        setPostScreenshotUrl(url)
        return () => { URL.revokeObjectURL(url); setPostScreenshotUrl(null) }
    }, [postScreenshotBlob])

    const invalidateList = () => {
        setPage(1)
        queryClient.invalidateQueries(['fund-transfers'])
    }

    const initiateMutation = useMutation({
        mutationFn: (payload) => initiateFundTransfer(payload),
        onSuccess: () => {
            toast.success('Transfer initiated. Funds are now in transit.')
            invalidateList()
            setShowInitiateModal(false)
            setInitiateForm(EMPTY_INITIATE_FORM)
            setPreTransferScreenshotFile(null)
        },
        onError: (err) => {
            const message = err?.response?.data?.message || 'Failed to initiate transfer.'
            toast.error(message)
        },
    })

    const confirmMutation = useMutation({
        mutationFn: ({ id, formData }) => confirmFundTransfer(id, formData),
        onSuccess: () => {
            toast.success('Transfer confirmed. Funds are now available in the destination account.')
            invalidateList()
            setShowConfirmModal(false)
            setTransferToAct(null)
            setPostTransferScreenshotFile(null)
        },
        onError: (err) => {
            const message = err?.response?.data?.message || 'Failed to confirm transfer.'
            toast.error(message)
        },
    })

    const cancelMutation = useMutation({
        mutationFn: (id) => cancelFundTransfer(id),
        onSuccess: () => {
            toast.success('Transfer cancelled. Funds returned to the source account.')
            invalidateList()
            setShowCancelModal(false)
            setTransferToAct(null)
        },
        onError: (err) => {
            const message = err?.response?.data?.message || 'Failed to cancel transfer.'
            toast.error(message)
        },
    })

    const openConfirmModal = (transfer) => {
        setTransferToAct(transfer)
        setPostTransferScreenshotFile(null)
        setShowConfirmModal(true)
    }

    const openCancelModal = (transfer) => {
        setTransferToAct(transfer)
        setShowCancelModal(true)
    }

    const closeViewModal = () => {
        setShowViewModal(false)
        setTransferToView(null)
        queryClient.removeQueries({ queryKey: ['fund-transfer-pre-screenshot'] })
        queryClient.removeQueries({ queryKey: ['fund-transfer-post-screenshot'] })
    }

    const openViewModal = (transfer) => {
        setTransferToView(transfer)
        setShowViewModal(true)
    }

    const handleInitiate = (e) => {
        e.preventDefault()
        const formData = new FormData()
        formData.append('direction', initiateForm.direction)
        formData.append('amount', parseInt(initiateForm.amount, 10))
        if (initiateForm.description) formData.append('description', initiateForm.description)
        formData.append('pre_transfer_screenshot', preTransferScreenshotFile)
        initiateMutation.mutate(formData)
    }

    const handleConfirm = (e) => {
        e.preventDefault()
        if (!postTransferScreenshotFile) return
        const formData = new FormData()
        formData.append('post_transfer_screenshot', postTransferScreenshotFile)
        confirmMutation.mutate({ id: transferToAct.id, formData })
    }

    const handleCancel = () => {
        cancelMutation.mutate(transferToAct.id)
    }

    const renderActionButtons = (transfer) => (
        <div className="d-flex gap-2 flex-wrap">
            <button
                className="btn btn-sm btn-outline-info"
                onClick={() => openViewModal(transfer)}
                aria-label={`View transfer ${transfer.id}`}
            >
                <i className="fa fa-eye me-1" aria-hidden="true" />
                View
            </button>
            {transfer.status === 'pending' && (
                <>
                    <button
                        className="btn btn-sm btn-outline-success"
                        onClick={() => openConfirmModal(transfer)}
                    >
                        <i className="fa fa-check me-1" aria-hidden="true" />
                        Confirm
                    </button>
                    <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => openCancelModal(transfer)}
                    >
                        <i className="fa fa-times me-1" aria-hidden="true" />
                        Cancel
                    </button>
                </>
            )}
        </div>
    )

    if (userRole !== ROLE_SLUGS.EXEC_ADMIN) {
        return (
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body text-center py-5">
                            <p className="text-muted mb-0">You do not have permission to view this page.</p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <Fragment>
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header d-flex align-items-center gap-2">
                            <h4 className="card-title mb-0">Fund Transfers</h4>
                            <button
                                type="button"
                                onClick={() => setShowInitiateModal(true)}
                                className="btn btn-sm btn-primary ms-auto d-none d-lg-inline-flex align-items-center"
                            >
                                <i className="fa fa-plus me-1" aria-hidden="true" />
                                New Transfer
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowInitiateModal(true)}
                                className="btn btn-primary rounded-circle ms-auto d-inline-flex d-lg-none align-items-center justify-content-center shadow-sm"
                                style={{ width: '2.5rem', height: '2.5rem', padding: 0 }}
                                title="New transfer"
                                aria-label="New transfer"
                            >
                                <i className="fas fa-plus" aria-hidden="true" />
                            </button>
                        </div>
                        <div className="card-body p-0">
                            {isPending ? (
                                <div className="d-flex justify-content-center py-4">
                                    <span className="spinner-border spinner-border-sm text-primary" role="status" />
                                </div>
                            ) : isError ? (
                                <p className="text-center text-danger py-4 mb-0">
                                    Failed to load fund transfers. Please try again.
                                </p>
                            ) : transfers.length === 0 ? (
                                <p className="text-center text-muted py-4 mb-0">
                                    No fund transfers found.
                                </p>
                            ) : (
                                <>
                                    <div className="table-responsive d-none d-xl-block px-3 pt-2">
                                        <table className="table verticle-middle mb-0">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Amount</th>
                                                    <th scope="col">Direction</th>
                                                    <th scope="col">Status</th>
                                                    <th scope="col">Description</th>
                                                    <th scope="col">Actor</th>
                                                    <th scope="col">Date</th>
                                                    <th scope="col" className="text-end">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {transfers.map((transfer) => (
                                                    <tr key={transfer.id}>
                                                        <td className="text-nowrap fw-semibold">{formatAmount(transfer.amount)}</td>
                                                        <td className="text-nowrap"><DirectionBadge direction={transfer.direction} /></td>
                                                        <td><StatusBadge status={transfer.status} /></td>
                                                        <td style={{ maxWidth: '240px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                            {transfer.description || '-'}
                                                        </td>
                                                        <td><ActorCell transfer={transfer} /></td>
                                                        <td className="text-nowrap">{formatDate(transfer.created_at)}</td>
                                                        <td className="text-end">{renderActionButtons(transfer)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="table-responsive d-none d-md-block d-xl-none px-3 pt-2">
                                        <table className="table verticle-middle mb-0">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Transfer</th>
                                                    <th scope="col">Status</th>
                                                    <th scope="col" className="text-end">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {transfers.map((transfer) => (
                                                    <tr key={transfer.id}>
                                                        <td>
                                                            <div className="fw-semibold">{formatAmount(transfer.amount)}</div>
                                                            <div className="mt-1"><DirectionBadge direction={transfer.direction} /></div>
                                                            {transfer.description && (
                                                                <div className="small text-muted mt-1" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                    {transfer.description}
                                                                </div>
                                                            )}
                                                            <div className="small text-muted mt-1">
                                                                <i className="bi bi-calendar-event me-1" aria-hidden="true" />
                                                                {formatDate(transfer.created_at)}
                                                            </div>
                                                            <div className="mt-2"><ActorCell transfer={transfer} /></div>
                                                        </td>
                                                        <td><StatusBadge status={transfer.status} /></td>
                                                        <td className="text-end">{renderActionButtons(transfer)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <ul className="list-group list-group-flush d-block d-md-none">
                                        {transfers.map((transfer) => (
                                            <li key={transfer.id} className="list-group-item py-3">
                                                <div className="d-flex align-items-center gap-3 mb-1 flex-wrap">
                                                    <span className="fw-semibold text-body">{formatAmount(transfer.amount)}</span>
                                                    <StatusBadge status={transfer.status} />
                                                </div>
                                                <div className="mt-1"><DirectionBadge direction={transfer.direction} /></div>
                                                {transfer.description && (
                                                    <div className="small text-muted mt-1" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                        {transfer.description}
                                                    </div>
                                                )}
                                                <div className="small text-muted mt-1">
                                                    <i className="bi bi-calendar-event me-1" aria-hidden="true" />
                                                    {formatDate(transfer.created_at)}
                                                </div>
                                                <div className="mt-2"><ActorCell transfer={transfer} /></div>
                                                <div className="mt-2">{renderActionButtons(transfer)}</div>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                            <PaginationBar
                                page={page}
                                lastPage={lastPage}
                                isLoading={isFetching}
                                onPrev={() => { setPage(p => Math.max(p - 1, 1)); scrollToPageTop(); }}
                                onNext={() => { setPage(p => Math.min(p + 1, lastPage)); scrollToPageTop(); }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <Modal show={showInitiateModal} onHide={() => { setShowInitiateModal(false); setInitiateForm(EMPTY_INITIATE_FORM); setPreTransferScreenshotFile(null) }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>New Fund Transfer</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {(() => {
                        const selected = DIRECTION_OPTIONS.find((o) => o.value === initiateForm.direction)
                        return (
                            <>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Direction <span className="text-danger">*</span></label>
                                    <div className="d-flex gap-3">
                                        {DIRECTION_OPTIONS.map((opt) => (
                                            <div key={opt.value} className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="direction"
                                                    id={`direction-${opt.value}`}
                                                    value={opt.value}
                                                    checked={initiateForm.direction === opt.value}
                                                    onChange={() => setInitiateForm({ ...initiateForm, direction: opt.value })}
                                                />
                                                <label className="form-check-label" htmlFor={`direction-${opt.value}`}>
                                                    {opt.label}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <p className="text-muted mb-3">
                                    Use this when you have moved funds from the <strong>{selected?.sourceAccount}</strong> account to the <strong>{selected?.destinationAccount}</strong> account on the Daraja dashboard. The amount will be held in transit until you confirm the transfer.
                                </p>
                                <form id="initiate-form" onSubmit={handleInitiate}>
                                    <div className="mb-3">
                                        <label className="form-label">Amount (KES) <span className="text-danger">*</span></label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            placeholder="e.g. 50000"
                                            min="1"
                                            step="1"
                                            value={initiateForm.amount}
                                            onChange={(e) => setInitiateForm({ ...initiateForm, amount: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Description <span className="text-muted">(optional)</span></label>
                                        <textarea
                                            className="form-control"
                                            rows={3}
                                            placeholder="e.g. Daraja reference #ABC123"
                                            maxLength={500}
                                            value={initiateForm.description}
                                            onChange={(e) => setInitiateForm({ ...initiateForm, description: e.target.value })}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Before-Transfer Screenshot <span className="text-danger">*</span></label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            accept=".jpg,.jpeg,.png"
                                            onChange={(e) => setPreTransferScreenshotFile(e.target.files?.[0] ?? null)}
                                            required
                                        />
                                        <div className="form-text">
                                            Screenshot of the <strong>{selected?.sourceAccount}</strong> account balance <strong>before</strong> initiating the transfer on Daraja.
                                        </div>
                                    </div>
                                </form>
                            </>
                        )
                    })()}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => { setShowInitiateModal(false); setInitiateForm(EMPTY_INITIATE_FORM); setPreTransferScreenshotFile(null) }}
                        disabled={initiateMutation.isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        form="initiate-form"
                        variant="primary"
                        disabled={initiateMutation.isPending || !initiateForm.amount || !preTransferScreenshotFile}
                    >
                        {initiateMutation.isPending ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                Submitting...
                            </>
                        ) : (
                            'Submit'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showConfirmModal} onHide={() => { setShowConfirmModal(false); setTransferToAct(null); setPostTransferScreenshotFile(null) }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Transfer</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {transferToAct && (
                        <div className="mb-3">
                            <p className="mb-1"><strong>Amount:</strong> {formatAmount(transferToAct.amount)}</p>
                            {transferToAct.description && (
                                <p className="mb-1"><strong>Description:</strong> {transferToAct.description}</p>
                            )}
                            <p className="mb-3"><strong>Initiated:</strong> {formatDate(transferToAct.created_at)}</p>
                        </div>
                    )}
                    <form id="confirm-form" onSubmit={handleConfirm}>
                        <div className="mb-3">
                            <label className="form-label">After-Transfer Screenshot <span className="text-danger">*</span></label>
                            <input
                                type="file"
                                className="form-control"
                                accept=".jpg,.jpeg,.png"
                                onChange={(e) => setPostTransferScreenshotFile(e.target.files?.[0] ?? null)}
                                required
                            />
                            <div className="form-text">
                                Screenshot of the <strong>{DIRECTION_OPTIONS.find((o) => o.value === transferToAct?.direction)?.destinationAccount ?? 'destination'}</strong> account balance <strong>after</strong> the transfer has completed on Daraja.
                            </div>
                        </div>
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => { setShowConfirmModal(false); setTransferToAct(null); setPostTransferScreenshotFile(null) }}
                        disabled={confirmMutation.isPending}
                    >
                        Close
                    </Button>
                    <Button
                        type="submit"
                        form="confirm-form"
                        variant="success"
                        disabled={confirmMutation.isPending || !postTransferScreenshotFile}
                    >
                        {confirmMutation.isPending ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                Confirming...
                            </>
                        ) : (
                            'Confirm Transfer'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showCancelModal} onHide={() => { setShowCancelModal(false); setTransferToAct(null) }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Cancel Transfer</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {transferToAct && (
                        <>
                            <p>
                                Are you sure you want to cancel this transfer? The funds will be returned to the{' '}
                                <strong>
                                    {DIRECTION_OPTIONS.find((o) => o.value === transferToAct.direction)?.sourceAccount ?? 'source'}
                                </strong>{' '}
                                account.
                            </p>
                            <p className="mb-1"><strong>Amount:</strong> {formatAmount(transferToAct.amount)}</p>
                            <p className="mb-1"><strong>Direction:</strong> {formatDirection(transferToAct.direction)}</p>
                            {transferToAct.description && (
                                <p className="mb-1"><strong>Description:</strong> {transferToAct.description}</p>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => { setShowCancelModal(false); setTransferToAct(null) }}
                        disabled={cancelMutation.isPending}
                    >
                        Close
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleCancel}
                        disabled={cancelMutation.isPending}
                    >
                        {cancelMutation.isPending ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                Cancelling...
                            </>
                        ) : (
                            'Yes, Cancel Transfer'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showViewModal} onHide={closeViewModal} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        Transfer #{transferToView?.id}
                        {transferToView && (
                            <span className={`badge badge-rounded ms-2 ${statusBadgeClass(transferToView.status)}`}>
                                {statusLabel(transferToView.status)}
                            </span>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {transferToView && (
                        <>
                            <div className="mb-4">
                                <p className="mb-1"><strong>Amount:</strong> {formatAmount(transferToView.amount)}</p>
                                <p className="mb-1"><strong>Direction:</strong> {formatDirection(transferToView.direction)}</p>
                                {transferToView.description && (
                                    <p className="mb-1"><strong>Description:</strong> {transferToView.description}</p>
                                )}
                                <p className="mb-1"><strong>Initiated by:</strong> {transferToView.initiated_by?.name ?? '-'}</p>
                                <p className="mb-0"><strong>Date:</strong> {formatDate(transferToView.created_at)}</p>
                            </div>
                            <div className="row g-3">
                                <div className="col-12 col-md-6">
                                    <p className="fw-semibold mb-2">Before Transfer</p>
                                    {preScreenshotPending ? (
                                        <p className="text-muted fst-italic">
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                            Loading...
                                        </p>
                                    ) : preScreenshotUrl ? (
                                        <a href={preScreenshotUrl} target="_blank" rel="noopener noreferrer">
                                            <img
                                                src={preScreenshotUrl}
                                                alt="Before-transfer balance screenshot"
                                                className="img-fluid rounded border"
                                                style={{ maxHeight: '320px', width: '100%', objectFit: 'contain' }}
                                            />
                                        </a>
                                    ) : (
                                        <p className="text-muted fst-italic">No screenshot available.</p>
                                    )}
                                </div>
                                <div className="col-12 col-md-6">
                                    <p className="fw-semibold mb-2">After Transfer</p>
                                    {transferToView.status === 'pending' ? (
                                        <p className="text-muted fst-italic">Awaiting confirmation.</p>
                                    ) : postScreenshotPending ? (
                                        <p className="text-muted fst-italic">
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                            Loading...
                                        </p>
                                    ) : postScreenshotUrl ? (
                                        <a href={postScreenshotUrl} target="_blank" rel="noopener noreferrer">
                                            <img
                                                src={postScreenshotUrl}
                                                alt="After-transfer balance screenshot"
                                                className="img-fluid rounded border"
                                                style={{ maxHeight: '320px', width: '100%', objectFit: 'contain' }}
                                            />
                                        </a>
                                    ) : (
                                        <p className="text-muted fst-italic">No screenshot available.</p>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeViewModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    )
}

export default FundTransfers
